import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { useApi } from '@/lib/auth'
import { usePreviewData } from '../preview-data'

const permissionSchema = z.object({ id: z.string(), name: z.string(), groupTag: z.string() })
const roleWithPermissionsSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  permissions: z.array(z.string()),
})

// GET /v1/admin/permissions -> { success, roles, permissions } (no `data` wrapper)
const permissionsEnvelopeSchema = z.object({
  success: z.boolean(),
  roles: z.array(roleWithPermissionsSchema),
  permissions: z.array(permissionSchema),
})
const okSchema = z.object({ success: z.boolean() }).passthrough()

export type AdminPermission = z.infer<typeof permissionSchema>
export type RoleWithPermissions = z.infer<typeof roleWithPermissionsSchema>
export interface PermissionMatrixData {
  roles: RoleWithPermissions[]
  permissions: AdminPermission[]
}
export type PermissionChange = { roleId: string; permissionId: string; action: 'grant' | 'revoke' }

export const adminPermissionsQueryKey = ['admin', 'permissions'] as const

/** Role x permission matrix data (also the scope catalog for api-tokens). */
export function useAdminPermissions() {
  const api = useApi()
  const preview = usePreviewData()
  return useQuery({
    queryKey: preview ? ['preview', 'admin-permissions'] : adminPermissionsQueryKey,
    queryFn: async (): Promise<PermissionMatrixData> => {
      const res = await api.get('/v1/admin/permissions', permissionsEnvelopeSchema)
      if (!res.success) throw new Error('Failed to fetch permissions')
      return { roles: res.roles, permissions: res.permissions }
    },
    enabled: !preview,
    ...(preview ? { initialData: preview.adminPermissions, staleTime: Number.POSITIVE_INFINITY } : {}),
  })
}

/** Pure: Map<roleId, Set<permissionId>> for O(1) cell lookups. */
export function grantedLookup(roles: RoleWithPermissions[]): Map<string, Set<string>> {
  return new Map(roles.map((r) => [r.id, new Set(r.permissions)]))
}

/** Pure: permissions grouped by `groupTag`, groups and members sorted. */
export function groupPermissions(
  permissions: AdminPermission[],
): { groupTag: string; permissions: AdminPermission[] }[] {
  const groups = new Map<string, AdminPermission[]>()
  for (const p of permissions) groups.set(p.groupTag, [...(groups.get(p.groupTag) ?? []), p])
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([groupTag, list]) => ({ groupTag, permissions: [...list].sort((a, b) => a.name.localeCompare(b.name)) }))
}

/** Pure: changes bringing `permissionIds` for `roleId` to all-granted (`grant`) or all-revoked; skips no-ops. */
export function bulkChanges(
  roles: RoleWithPermissions[],
  roleId: string,
  permissionIds: string[],
  grant: boolean,
): PermissionChange[] {
  const have = grantedLookup(roles).get(roleId) ?? new Set<string>()
  return permissionIds
    .filter((id) => have.has(id) !== grant)
    .map((permissionId) => ({ roleId, permissionId, action: grant ? 'grant' : 'revoke' }))
}

/** Pure: apply changes to matrix data (optimistic update helper). */
export function applyChanges(data: PermissionMatrixData, changes: PermissionChange[]): PermissionMatrixData {
  return {
    ...data,
    roles: data.roles.map((r) => {
      const mine = changes.filter((c) => c.roleId === r.id)
      if (mine.length === 0) return r
      const set = new Set(r.permissions)
      for (const c of mine) {
        if (c.action === 'grant') set.add(c.permissionId)
        else set.delete(c.permissionId)
      }
      return { ...r, permissions: [...set] }
    }),
  }
}

function useOptimisticPermissionMutation<V>(run: (v: V) => Promise<unknown>, changesOf: (v: V) => PermissionChange[]) {
  const qc = useQueryClient()
  const preview = usePreviewData()
  return useMutation({
    mutationFn: async (v: V) => {
      if (preview) return { success: true }
      return run(v)
    },
    onMutate: async (v) => {
      if (preview) return { previous: undefined }
      await qc.cancelQueries({ queryKey: adminPermissionsQueryKey })
      const previous = qc.getQueryData<PermissionMatrixData>(adminPermissionsQueryKey)
      if (previous) qc.setQueryData(adminPermissionsQueryKey, applyChanges(previous, changesOf(v)))
      return { previous }
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(adminPermissionsQueryKey, ctx.previous)
    },
    onSettled: async () => {
      if (!preview) await qc.invalidateQueries({ queryKey: adminPermissionsQueryKey })
    },
  })
}

/**
 * Grant/revoke one cell. `mutate({ roleId, permissionId, action })` PATCH /v1/admin/role-permissions.
 * Optimistic: the cached matrix updates immediately and reverts on error. The API RBAC cache is KV (300s TTL).
 */
export function useUpdateRolePermission() {
  const api = useApi()
  return useOptimisticPermissionMutation<PermissionChange>(
    (v) => api.request('/v1/admin/role-permissions', { method: 'PATCH', body: v }, okSchema),
    (v) => [v],
  )
}

/** Row/column toggle. `mutate({ changes })` (1..100) PATCH /v1/admin/role-permissions/bulk; build with `bulkChanges`. */
export function useBulkUpdateRolePermissions() {
  const api = useApi()
  return useOptimisticPermissionMutation<{ changes: PermissionChange[] }>(
    (v) =>
      api.request(
        '/v1/admin/role-permissions/bulk',
        { method: 'PATCH', body: v },
        z.object({ success: z.boolean(), applied: z.number() }),
      ),
    (v) => v.changes,
  )
}
